import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import panelApi from "@/lib/panelApi";
import { formatTr } from "@/lib/date";
import { useAcenteAuth } from "@/context/AcenteAuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  CheckCircle2, AlertTriangle, Search, Download, ArrowRight,
  Users, BedDouble, Calendar, Phone, Hotel as HotelIcon, Utensils,
  Coins, StickyNote, ClipboardCheck, RotateCcw,
} from "lucide-react";
import * as XLSX from "xlsx";

/**
 * Admin reservation tracking — one card per reservation, ALL fields
 * visible so the admin can manually copy each row into an external Excel
 * spreadsheet without opening the detail page. Every card has an
 * "Excel'e Girildi" toggle: once clicked, the card gets a green tint +
 * checkmark so the admin knows they already processed it. The flag lives
 * on the reservation itself (`excel_exported`) so it survives refresh /
 * device change.
 */

const STATUS_LABEL = {
  taslak: "Taslak",
  eksik_bilgi: "Eksik Bilgi",
  incelemede: "İncelemede",
  onaylandi: "Onaylı",
  iptal: "İptal",
};
const STATUS_TINT = {
  taslak: "bg-slate-100 text-slate-700 border-slate-200",
  eksik_bilgi: "bg-orange-100 text-orange-900 border-orange-200",
  incelemede: "bg-amber-100 text-amber-900 border-amber-200",
  onaylandi: "bg-emerald-100 text-emerald-800 border-emerald-200",
  iptal: "bg-rose-100 text-rose-800 border-rose-200",
};
const MEAL_LABEL = { kahvalti: "Kahvaltı", ogle: "Öğle (eski)", aksam: "Akşam" };

function isFilled(r) {
  const rm = r.rooms || {};
  const rooms = (rm.r1||0) + (rm.r2||0) + (rm.r3||0) + (rm.r4||0) + (rm.r5||0);
  const heads = (r.adults||0) + (r.children||0) + (r.staff||0);
  return !!(r.group_name && r.checkin && r.checkout && rooms > 0 && heads > 0);
}

function formatDateTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("tr-TR", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return ""; }
}

/**
 * Return today's / tomorrow's date as `YYYY-MM-DD` in the user's local
 * timezone — reservations store `checkin` in the same shape so string
 * comparison is safe.
 */
function ymd(offsetDays = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function AcenteReservationTracking() {
  const { user } = useAcenteAuth();
  // Admin gets a stripped-down card view (per user request):
  //   • Header: only agency + group (no rez number, status pill or badges)
  //   • Detail grid: only date/nights, meal range, adults+staff count,
  //     nightly room price and nightly meal price
  //   • No customer phone, hotel row, total price, room-mix breakdown,
  //     notes or ops-note.
  // Operasyon/acente keep the rich view they already had.
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [agencies, setAgencies] = useState({});
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [exportedFilter, setExportedFilter] = useState("all"); // all | done | pending
  const [onlyMissing, setOnlyMissing] = useState(false);
  // Iter 41: Fiyat kilit filtresi — admin operasyondan onay bekleyen kayıtları hızlıca görsün
  const [lockedFilter, setLockedFilter] = useState("all"); // all | locked | pending
  const [markingId, setMarkingId] = useState(null);
  // Date-range quick-tab. Default is "tomorrow" because meal orders for
  // next-day arrivals is the highest-priority daily task per the operator.
  const [dateTab, setDateTab] = useState("tomorrow");

  const loadRows = useCallback(() => {
    panelApi.get("/reservations").then((r) => setRows(r.data || [])).catch(() => setRows([]));
  }, []);

  useEffect(() => {
    loadRows();
    panelApi.get("/agencies").then((r) => {
      const m = {}; (r.data || []).forEach((a) => { m[a.id] = a.name; });
      setAgencies(m);
    }).catch(() => {});
  }, [loadRows]);

  // Pre-compute today's/tomorrow's/week-end YMD strings (memoised once per
  // render since Date.now doesn't tick fast enough to matter).
  const todayStr = ymd(0);
  const tomorrowStr = ymd(1);
  const weekEndStr = ymd(7);

  // Bucket counts for the tab strip — always over the full unfiltered
  // list so admin sees the pipeline at a glance regardless of active tab.
  const bucketCounts = useMemo(() => {
    const c = { tomorrow: 0, today: 0, week: 0, all: rows.length };
    for (const r of rows) {
      const ck = r.checkin || "";
      if (ck === tomorrowStr) c.tomorrow += 1;
      if (ck === todayStr) c.today += 1;
      if (ck >= todayStr && ck <= weekEndStr) c.week += 1;
    }
    return c;
  }, [rows, todayStr, tomorrowStr, weekEndStr]);

  const filtered = useMemo(() => {
    let out = [...rows];
    // Date-range tab — applied first so all other filters operate on a
    // pre-narrowed set.
    if (dateTab === "tomorrow") out = out.filter((r) => (r.checkin || "") === tomorrowStr);
    else if (dateTab === "today") out = out.filter((r) => (r.checkin || "") === todayStr);
    else if (dateTab === "week") out = out.filter((r) => {
      const ck = r.checkin || "";
      return ck >= todayStr && ck <= weekEndStr;
    });

    const s = q.trim().toLowerCase();
    if (s) {
      out = out.filter((r) =>
        (r.reservation_number || "").toLowerCase().includes(s) ||
        (r.group_name || "").toLowerCase().includes(s) ||
        (r.customer_phone || "").toLowerCase().includes(s) ||
        (r.hotel_name || "").toLowerCase().includes(s) ||
        (agencies[r.agency_id] || "").toLowerCase().includes(s)
      );
    }
    if (statusFilter !== "all") out = out.filter((r) => r.status === statusFilter);
    if (onlyMissing) out = out.filter((r) => r.status === "eksik_bilgi" || !isFilled(r));
    if (exportedFilter === "done") out = out.filter((r) => r.excel_exported);
    if (exportedFilter === "pending") out = out.filter((r) => !r.excel_exported);
    if (lockedFilter === "locked") out = out.filter((r) => (r.total_price || 0) > 0);
    if (lockedFilter === "pending") out = out.filter((r) => !((r.total_price || 0) > 0));
    // Earliest arrivals on top — matches Excel workflow (upcoming first)
    out.sort((a, b) => (a.checkin || "").localeCompare(b.checkin || ""));
    return out;
  }, [rows, q, statusFilter, onlyMissing, exportedFilter, lockedFilter, agencies, dateTab, todayStr, tomorrowStr, weekEndStr]);

  const toggleExported = async (r) => {
    setMarkingId(r.id);
    const next = !r.excel_exported;
    // Optimistic UI — flip locally first, revert on error.
    setRows((prev) => prev.map((x) => (x.id === r.id ? { ...x, excel_exported: next } : x)));
    try {
      const resp = await panelApi.patch(`/reservations/${r.id}/excel-exported`, { exported: next });
      // Server returns the actual value; reflect it (in case admin double-clicked).
      const serverVal = !!resp?.data?.excel_exported;
      setRows((prev) => prev.map((x) => (x.id === r.id ? { ...x, excel_exported: serverVal } : x)));
      toast.success(serverVal ? "Excel'e girildi olarak işaretlendi" : "İşaret kaldırıldı");
      // Refetch just this row so we get the timestamp + by-name fields.
      if (serverVal) {
        try {
          const one = await panelApi.get(`/reservations/${r.id}`);
          setRows((prev) => prev.map((x) => (x.id === r.id ? one.data : x)));
        } catch { /* ignore */ }
      }
    } catch (err) {
      // Revert on error
      setRows((prev) => prev.map((x) => (x.id === r.id ? { ...x, excel_exported: !next } : x)));
      toast.error(err?.response?.data?.detail || "İşlem başarısız");
    } finally {
      setMarkingId(null);
    }
  };

  const exportXlsx = () => {
    const header = [
      "#", "Girildi", "Excel'e Aktarıldı", "Rez.No", "Acente", "Grup Adı", "Müşteri Tel", "Otel",
      "Giriş", "Çıkış", "Gece", "Yetişkin", "Çocuk", "Görevli",
      "1K", "2K", "3K", "4K", "5K",
      "Öğün Başlangıç", "Öğün Bitiş",
      "Oda Birim (SAR)", "Yemek Birim (SAR)", "Toplam (SAR)",
      "Durum", "Not",
    ];
    const data = filtered.map((r, i) => {
      const rm = r.rooms || {};
      return [
        i + 1,
        isFilled(r) ? "✓" : "",
        r.excel_exported ? "✓" : "",
        r.reservation_number, agencies[r.agency_id] || "", r.group_name || "",
        r.customer_phone || "", r.hotel_name || "",
        formatTr(r.checkin), formatTr(r.checkout), r.nights,
        r.adults || 0, r.children || 0, r.staff || 0,
        rm.r1||0, rm.r2||0, rm.r3||0, rm.r4||0, rm.r5||0,
        MEAL_LABEL[r.meal_start] || r.meal_start || "",
        MEAL_LABEL[r.meal_end] || r.meal_end || "",
        r.agreed_room_price || 0, r.agreed_meal_price || 0, r.total_price || 0,
        STATUS_LABEL[r.status] || r.status,
        r.notes || "",
      ];
    });
    const ws = XLSX.utils.aoa_to_sheet([header, ...data]);
    ws["!cols"] = header.map((h) => ({ wch: Math.max(h.length + 2, 10) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Rezervasyonlar");
    XLSX.writeFile(wb, `rezervasyon-takip-${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  const total = filtered.length;
  const filledCount = filtered.filter(isFilled).length;
  const missingCount = filtered.filter((r) => r.status === "eksik_bilgi" || !isFilled(r)).length;
  const exportedCount = filtered.filter((r) => r.excel_exported).length;

  return (
    <div className="space-y-5" data-testid="acente-reservation-tracking">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-brand-orange font-semibold mb-2">Rezervasyon Takip</div>
          <h1 className="font-serif text-3xl text-brand-navy">Tüm Detayları Tek Ekranda</h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Her rezervasyonun tüm bilgisi kartta açık — kolayca kendi Excel'inize kopyalayabilirsiniz.
            Bir satırı işledikten sonra <b>Excel'e Girildi</b> butonuna basın; kart yeşile döner ve
            hangi kayıtları hallettiğinizi kaybetmezsiniz.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="rounded-2xl bg-white border border-brand-navy/10 px-3 py-2 text-xs flex items-center gap-3">
            <span data-testid="tracking-count-total"><span className="font-semibold text-brand-navy">{total}</span> kayıt</span>
            <span className="text-emerald-700"><CheckCircle2 size={11} className="inline mb-0.5" /> {filledCount} tam</span>
            <span className="text-orange-700"><AlertTriangle size={11} className="inline mb-0.5" /> {missingCount} eksik</span>
            <span className="text-brand-navy"><ClipboardCheck size={11} className="inline mb-0.5" /> {exportedCount} girildi</span>
          </div>
          <Button variant="outline" onClick={exportXlsx} data-testid="tracking-export-xlsx">
            <Download size={14} className="mr-1" /> Excel Dosyası İndir
          </Button>
        </div>
      </header>

      {/* Date quick-tabs — Yarın first because tomorrow's meal orders are
          the most important daily task. Also shows a friendly explanation
          right next to the active tab. */}
      <div className="bg-white rounded-2xl border border-brand-navy/10 overflow-hidden" data-testid="tracking-date-tabs">
        <div role="tablist" aria-label="Giriş tarihine göre filtrele" className="flex flex-wrap gap-0 divide-x divide-slate-100">
          {[
            { key: "tomorrow", label: "Yarınki Girişler", sub: "yemek listesi", count: bucketCounts.tomorrow, accent: "brand-orange" },
            { key: "today",    label: "Bugünkü Girişler", sub: "check-in",     count: bucketCounts.today,    accent: "emerald-600" },
            { key: "week",     label: "Bu Hafta",         sub: "önümüzdeki 7 gün", count: bucketCounts.week,  accent: "brand-navy" },
            { key: "all",      label: "Tümü",             sub: "geçmiş + gelecek", count: bucketCounts.all,   accent: "slate-600" },
          ].map((t) => {
            const active = dateTab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setDateTab(t.key)}
                data-testid={`tracking-date-tab-${t.key}`}
                className={`flex-1 min-w-[140px] px-4 py-3 text-left transition group ${
                  active ? "bg-canvas-cream" : "hover:bg-canvas-cream/60"
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-semibold ${active ? "text-brand-navy" : "text-slate-700"}`}>{t.label}</span>
                  <span className={`text-[10px] font-mono rounded-full px-1.5 py-0.5 ${
                    active
                      ? "bg-brand-orange text-white"
                      : "bg-slate-100 text-slate-600 group-hover:bg-slate-200"
                  }`}>
                    {t.count}
                  </span>
                </div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">{t.sub}</div>
                {active && (
                  <div className={`h-0.5 mt-2 -mb-3 bg-brand-orange`} aria-hidden />
                )}
              </button>
            );
          })}
        </div>
        {dateTab === "tomorrow" && (
          <div className="border-t border-slate-100 bg-brand-orange/5 px-4 py-2 text-xs text-brand-navy flex items-center gap-2" data-testid="tracking-tomorrow-hint">
            <Calendar size={13} className="text-brand-orange" />
            <span><b>Yarın ({formatTr(tomorrowStr)})</b> giriş yapacak {bucketCounts.tomorrow} grup — yemek listelerini bugünden hazırlayın.</span>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-brand-navy/10 p-3 flex flex-wrap items-center gap-3" data-testid="tracking-filters">
        <div className="relative flex-1 min-w-[260px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rez.No, grup, acente, otel, telefon ara..." className="pl-8" data-testid="tracking-search" />
        </div>
        <select
          value={exportedFilter}
          onChange={(e) => setExportedFilter(e.target.value)}
          className="flex h-10 rounded-md border border-input bg-transparent px-3 py-2 text-sm"
          data-testid="tracking-exported-filter"
        >
          <option value="all">Excel: Tümü</option>
          <option value="pending">Bekleyenler (girilmemiş)</option>
          <option value="done">Girildi olanlar</option>
        </select>
        <select
          value={lockedFilter}
          onChange={(e) => setLockedFilter(e.target.value)}
          className="flex h-10 rounded-md border border-input bg-transparent px-3 py-2 text-sm"
          data-testid="tracking-locked-filter"
        >
          <option value="all">Fiyat: Tümü</option>
          <option value="locked">🔒 Kilitli olanlar</option>
          <option value="pending">⏳ Onay Bekleyenler</option>
        </select>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={onlyMissing}
            onChange={(e) => setOnlyMissing(e.target.checked)}
            className="rounded"
            data-testid="tracking-only-missing"
          />
          Sadece eksik bilgi
        </label>
      </div>

      {/* Card list */}
      <div className="space-y-3" data-testid="tracking-card-list">
        {filtered.length === 0 && (
          <div className="rounded-2xl bg-white border border-brand-navy/10 p-10 text-center text-slate-500" data-testid="tracking-empty">
            {dateTab === "tomorrow" && (
              <>
                <div className="text-3xl mb-2">🌙</div>
                <div className="text-sm">Yarın giriş yapacak grup yok.</div>
                <div className="text-xs mt-1 text-slate-400">Rahat bir gün — bugünden yeni rezervasyon eklenirse burada belirir.</div>
              </>
            )}
            {dateTab === "today" && (
              <>
                <div className="text-3xl mb-2">☀️</div>
                <div className="text-sm">Bugün giriş yapan grup yok.</div>
              </>
            )}
            {dateTab === "week" && <div className="text-sm">Önümüzdeki 7 günde giriş yapan grup yok.</div>}
            {dateTab === "all" && <div className="text-sm">Kriterlere uyan rezervasyon yok.</div>}
          </div>
        )}
        {filtered.map((r, i) => {
          const filled = isFilled(r);
          const missing = r.status === "eksik_bilgi" || !filled;
          const rm = r.rooms || {};
          const totalRooms = (rm.r1||0) + (rm.r2||0) + (rm.r3||0) + (rm.r4||0) + (rm.r5||0);
          const totalHeads = (r.adults||0) + (r.children||0) + (r.staff||0);
          const done = !!r.excel_exported;
          const cardTint = done
            ? "bg-emerald-50/70 border-emerald-300"
            : missing
              ? "bg-orange-50/60 border-orange-200"
              : "bg-white border-brand-navy/10";
          return (
            <div
              key={r.id}
              className={`rounded-2xl border ${cardTint} shadow-sm hover:shadow-md transition p-4 sm:p-5`}
              data-testid={`tracking-card-${r.id}`}
            >
              {/* Top row: admin sees a minimal header (agency + group only,
                  per user spec) with just the Excel toggle. Non-admin
                  users keep the full pipeline view (rez number, status,
                  badges, detail link, excel state). */}
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  {!isAdmin && (
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${done ? "bg-emerald-500 text-white" : "bg-brand-navy/5 text-brand-navy"}`}>
                      <span className="text-xs font-semibold">{i + 1}</span>
                    </div>
                  )}
                  <div className="min-w-0">
                    {!isAdmin && (
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs bg-brand-navy/5 text-brand-navy px-2 py-0.5 rounded" data-testid={`tracking-rez-num-${r.id}`}>
                          {r.reservation_number}
                        </span>
                        {r.is_revised && (
                          <span className="text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 border bg-amber-100 text-amber-800 border-amber-200"
                            data-testid={`tracking-revised-${r.id}`}>
                            ✎ Revize
                          </span>
                        )}
                        {done && (
                          <span className="text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 bg-emerald-500 text-white inline-flex items-center gap-1"
                            data-testid={`tracking-exported-badge-${r.id}`}>
                            <ClipboardCheck size={10}/> Excel'e Girildi
                          </span>
                        )}
                        {missing && !done && (
                          <span className="text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 bg-orange-500 text-white inline-flex items-center gap-1">
                            <AlertTriangle size={10}/> Eksik
                          </span>
                        )}
                      </div>
                    )}
                    <div className={isAdmin ? "flex items-center gap-2 flex-wrap" : "mt-1 flex items-baseline gap-2 flex-wrap"}>
                      <div className="font-serif text-xl text-brand-navy leading-tight" data-testid={`tracking-agency-${r.id}`}>
                        {agencies[r.agency_id] || "Bağımsız"}
                      </div>
                      {r.group_name && (
                        <div className="text-sm text-slate-500" data-testid={`tracking-group-${r.id}`}>
                          · {r.group_name}
                        </div>
                      )}
                      {/* Iter 41: Admin için fiyat kilit chip'i — grubun fiyatı operasyondan onay bekliyor mu? */}
                      {(r.total_price || 0) > 0 ? (
                        <span className="text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 border bg-emerald-100 text-emerald-800 border-emerald-200 inline-flex items-center gap-1"
                          data-testid={`tracking-price-locked-${r.id}`}
                          title="Operasyon fiyatı onayladı ve kilitledi">
                          🔒 Fiyat Kilitli
                        </span>
                      ) : (
                        <span className="text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 border bg-slate-100 text-slate-600 border-slate-200 inline-flex items-center gap-1"
                          data-testid={`tracking-price-pending-${r.id}`}
                          title="Operasyon henüz fiyatı kilitlemedi">
                          ⏳ Fiyat Bekliyor
                        </span>
                      )}
                      {r.is_revised && isAdmin && (
                        <span className="text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 border bg-amber-100 text-amber-800 border-amber-200"
                          data-testid={`tracking-admin-revised-${r.id}`}>
                          ✎ Revize
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                {/* Right side: mark toggle + open detail (detail link hidden for admin). */}
                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    size="sm"
                    onClick={() => toggleExported(r)}
                    disabled={markingId === r.id}
                    className={done
                      ? "bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200"
                      : "bg-emerald-600 hover:bg-emerald-700 text-white"
                    }
                    data-testid={`tracking-mark-exported-${r.id}`}
                    title={done ? "İşareti kaldır — geri getir" : "Kaydı Excel'inize kopyaladığınızda tıklayın"}
                  >
                    {done ? (
                      <><RotateCcw size={13} className="mr-1" /> İşareti Kaldır</>
                    ) : (
                      <><ClipboardCheck size={13} className="mr-1" /> Excel'e Girildi</>
                    )}
                  </Button>
                  {!isAdmin && (
                    <Link
                      to={`/acente/rezervasyon/${r.id}`}
                      className="text-xs px-3 py-2 rounded-md bg-brand-navy/5 hover:bg-brand-navy/10 text-brand-navy inline-flex items-center gap-1"
                      data-testid={`tracking-open-${r.id}`}
                    >
                      Detay <ArrowRight size={12} />
                    </Link>
                  )}
                </div>
              </div>

              {/* Detail grid — admin gets a slim 5-field grid (dates,
                  meal range, adults+staff count, room unit price, meal
                  unit price). Everything else (phone, hotel, room mix,
                  total, notes) is intentionally omitted. */}
              {isAdmin ? (
                <div className="mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                  <DetailField
                    icon={<Calendar size={13} />}
                    label="Giriş / Çıkış"
                    value={
                      <>
                        <span className="font-mono">{formatTr(r.checkin) || "—"}</span>
                        <span className="text-slate-400 mx-1">→</span>
                        <span className="font-mono">{formatTr(r.checkout) || "—"}</span>
                      </>
                    }
                    sub={r.nights ? `${r.nights} gece` : ""}
                    testid={`tracking-dates-${r.id}`}
                  />
                  <DetailField
                    icon={<Utensils size={13} />}
                    label="Öğün Aralığı"
                    value={
                      <>
                        {MEAL_LABEL[r.meal_start] || r.meal_start || "—"}
                        <span className="text-slate-400 mx-1">→</span>
                        {MEAL_LABEL[r.meal_end] || r.meal_end || "—"}
                      </>
                    }
                    testid={`tracking-meals-${r.id}`}
                  />
                  <DetailField
                    icon={<Users size={13} />}
                    label="Yemek Yiyen"
                    value={<><b>{(r.adults || 0) + (r.staff || 0)}</b> kişi</>}
                    sub={`${r.adults || 0} yetişkin · ${r.staff || 0} görevli`}
                    testid={`tracking-heads-${r.id}`}
                  />
                  <DetailField
                    icon={<Coins size={13} />}
                    label="Gecelik Oda"
                    value={r.agreed_room_price
                      ? <><b className="text-brand-navy">{Number(r.agreed_room_price).toLocaleString("tr-TR")}</b> <span className="text-slate-500 text-xs">﷼/gece</span></>
                      : <span className="italic text-slate-400">—</span>}
                    testid={`tracking-room-unit-${r.id}`}
                  />
                  <DetailField
                    icon={<Coins size={13} />}
                    label="Gecelik Yemek"
                    value={r.agreed_meal_price
                      ? <><b className="text-brand-navy">{Number(r.agreed_meal_price).toLocaleString("tr-TR")}</b> <span className="text-slate-500 text-xs">﷼/kişi</span></>
                      : <span className="italic text-slate-400">—</span>}
                    testid={`tracking-meal-unit-${r.id}`}
                  />
                </div>
              ) : (
                <div className="mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  <DetailField
                    icon={<Calendar size={13} />}
                    label="Giriş / Çıkış"
                    value={
                      <>
                        <span className="font-mono">{formatTr(r.checkin) || "—"}</span>
                        <span className="text-slate-400 mx-1">→</span>
                        <span className="font-mono">{formatTr(r.checkout) || "—"}</span>
                      </>
                    }
                    sub={r.nights ? `${r.nights} gece` : ""}
                    testid={`tracking-dates-${r.id}`}
                  />
                  <DetailField
                    icon={<Users size={13} />}
                    label="Kişi Sayısı"
                    value={<><b>{totalHeads}</b> kişi</>}
                    sub={`${r.adults||0}Y · ${r.children||0}Ç · ${r.staff||0}G`}
                    testid={`tracking-heads-${r.id}`}
                  />
                  <DetailField
                    icon={<BedDouble size={13} />}
                    label="Oda Dağılımı"
                    value={<><b>{totalRooms}</b> oda</>}
                    sub={`1K:${rm.r1||0} · 2K:${rm.r2||0} · 3K:${rm.r3||0} · 4K:${rm.r4||0} · 5K:${rm.r5||0}`}
                    testid={`tracking-rooms-${r.id}`}
                  />
                  <DetailField
                    icon={<HotelIcon size={13} />}
                    label="Otel"
                    value={r.hotel_name || <span className="italic text-slate-400">Belirtilmemiş</span>}
                    testid={`tracking-hotel-${r.id}`}
                  />
                  <DetailField
                    icon={<Phone size={13} />}
                    label="Müşteri Tel."
                    value={r.customer_phone
                      ? <a href={`tel:${r.customer_phone}`} className="hover:text-brand-orange">{r.customer_phone}</a>
                      : <span className="italic text-slate-400">—</span>}
                    testid={`tracking-phone-${r.id}`}
                  />
                  <DetailField
                    icon={<Utensils size={13} />}
                    label="Öğün Aralığı"
                    value={
                      <>
                        {MEAL_LABEL[r.meal_start] || r.meal_start || "—"}
                        <span className="text-slate-400 mx-1">→</span>
                        {MEAL_LABEL[r.meal_end] || r.meal_end || "—"}
                      </>
                    }
                    testid={`tracking-meals-${r.id}`}
                  />
                  <DetailField
                    icon={<Coins size={13} />}
                    label="Oda Birim Fiyat"
                    value={r.agreed_room_price
                      ? <><b className="text-brand-navy">{Number(r.agreed_room_price).toLocaleString("tr-TR")}</b> <span className="text-slate-500 text-xs">﷼/gece</span></>
                      : <span className="italic text-slate-400">—</span>}
                    testid={`tracking-room-unit-${r.id}`}
                  />
                  <DetailField
                    icon={<Coins size={13} />}
                    label="Yemek Birim Fiyat"
                    value={r.agreed_meal_price
                      ? <><b className="text-brand-navy">{Number(r.agreed_meal_price).toLocaleString("tr-TR")}</b> <span className="text-slate-500 text-xs">﷼/kişi</span></>
                      : <span className="italic text-slate-400">—</span>}
                    testid={`tracking-meal-unit-${r.id}`}
                  />
                  <DetailField
                    icon={<Coins size={13} />}
                    label="Toplam Tutar"
                    value={r.total_price
                      ? <><b className="text-brand-navy text-base">{Number(r.total_price).toLocaleString("tr-TR")}</b> <span className="text-slate-500 text-xs">﷼</span></>
                      : <span className="italic text-slate-400">—</span>}
                    testid={`tracking-total-${r.id}`}
                  />
                </div>
              )}

              {/* Notes — full width; hidden entirely for admin (per user spec). */}
              {!isAdmin && (r.notes || r.ops_note) && (
                <div className="mt-3 border-t border-slate-100 pt-3 flex items-start gap-2 text-sm">
                  <StickyNote size={13} className="text-slate-400 mt-0.5 flex-shrink-0" />
                  <div className="min-w-0">
                    {r.notes && (
                      <div className="text-slate-700 whitespace-pre-wrap" data-testid={`tracking-notes-${r.id}`}>
                        <span className="text-[10px] uppercase tracking-widest text-slate-500 mr-2">Not</span>
                        {r.notes}
                      </div>
                    )}
                    {r.ops_note && (
                      <div className="text-amber-800 whitespace-pre-wrap mt-1" data-testid={`tracking-ops-note-${r.id}`}>
                        <span className="text-[10px] uppercase tracking-widest text-amber-700 mr-2">Ops Notu</span>
                        {r.ops_note}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {done && r.excel_exported_at && (
                <div className="mt-3 text-[11px] text-emerald-800/80 bg-emerald-100/60 rounded-md px-3 py-1.5 inline-flex items-center gap-1.5">
                  <CheckCircle2 size={11} />
                  <span>
                    {r.excel_exported_by_name ? `${r.excel_exported_by_name} tarafından ` : ""}
                    <span className="font-mono">{formatDateTime(r.excel_exported_at)}</span> tarihinde Excel'e girildi
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DetailField({ icon, label, value, sub, testid }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1">
        {icon} {label}
      </div>
      <div className="text-sm text-brand-navy mt-0.5 truncate" data-testid={testid} title={typeof value === "string" ? value : undefined}>
        {value}
      </div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5 truncate">{sub}</div>}
    </div>
  );
}
